import React, { Dispatch, SetStateAction } from "react";
import { Menu, MenuProps } from "antd";

import { THEME_TYPE_LITE } from "../../../../constants/ApplicationConstants/ThemeSetting";
import { SideMenuItemKeys } from "../../../../constants/ApplicationConstants/RouteConstants";
import { LocalStorageConstants } from "../../../../constants/ApplicationConstants/LocalStorageConstants";
import LocalStorageUtil from "../../../../util/LocalStorageUtil";
import Authentication from "../../../../util/Authentication";
import { Modules, Operations } from "../../../../constants/DatabaseConstants/MasterConstant";
import SidebarLogo from "../SideBarLogo";
import { useSideBarContent } from "./index.hook";
import styles from "./index.module.less";

import type { UserInterface } from "../../../../shared/interface/auth/User.interface";
import FixMeLater from "../../../../shared/types/fixMeLater.type";
import CustomScrollbars from "../../../../util/CustomScrollbars";
import { useSelector } from "react-redux";
import { RootState } from "../../../../appRedux/store";
import { UserRoleDATA } from "../../../../constants/DatabaseConstants/AuthConstants";

type SidebarContentProps = {
  sidebarCollapsed: boolean;
  setSidebarCollapsed: Dispatch<SetStateAction<boolean>>;
  isSmallScreen: boolean;
};

type MenuItem = Required<MenuProps>["items"][number];

const getItem = (label: React.ReactNode, key: React.Key, icon?: React.ReactNode, children?: MenuItem[], type?: "group"): MenuItem => {
  return {
    key,
    icon,
    children,
    label,
    type
  } as MenuItem;
};

const generateHomeSettings = (openCount: number) => {
  const items: FixMeLater = [];

  const userData = LocalStorageUtil.localStorageGetItem(LocalStorageConstants.USER_DATA) as UserInterface;

  if (!userData) return items;

  if (Authentication.hasModuleAndOperationAccess(userData, Modules.DASHBOARD, Operations.READ)) {
    items.push(getItem("Dashboard", SideMenuItemKeys.DEFAULT_HOME_PAGE, <i className="icon icon-home" />));
    items.push(getItem("Client Parking Availability", SideMenuItemKeys.CLIENT_PARKING_AVAILABILITY_LIST, <i className="icon icon-calendar" />));
  }

  /* WP-Admin Access Only */
  if (userData?.role === UserRoleDATA.PARI_ADMIN) {
    items.push(getItem("GW & PLC Status List", SideMenuItemKeys.LOCATION_STATUS_LIST, <i className="icon icon-calendar" />));
  }

  if (Authentication.hasModuleAndOperationAccess(userData, Modules.ONGOING_LIST, Operations.READ)) {
    items.push(getItem("Ongoing Queue", SideMenuItemKeys.DISPLAY_QUEUE_STATUS, <i className="icon icon-all-contacts" />));
  }

  if (Authentication.hasModuleAndOperationAccess(userData, Modules.SCHEDULE_LIST, Operations.READ)) {
    items.push(getItem("Scheduled List", SideMenuItemKeys.DISPLAY_SCHEDULE_LIST, <i className="icon icon-schedule" />));
  }

  if (Authentication.hasModuleAndOperationAccess(userData, Modules.EMERGENCY_RETRIEVAL_REQUEST, Operations.READ)) {
    items.push(getItem("Emergency Retrieval", SideMenuItemKeys.EMERGENCY_RETRIEVAL_REQUEST, <i className="icon icon-product-list" />));
  }

  if (Authentication.hasModuleAndOperationAccess(userData, Modules.PARKING_OCCUPANCY_MONITORING, Operations.READ)) {
    items.push(getItem("Tower Occupancy", SideMenuItemKeys.PARKING_OCCUPANCY_MONITORING, <i className="icon icon-product-list" />));
  }

  if (Authentication.hasModuleAndOperationAccess(userData, Modules.DEAD_PARKING_UPDATE, Operations.READ)) {
    items.push(getItem("Dead Parking", SideMenuItemKeys.DEAD_PARKING_UPDATE, <i className="icon icon-product-list" />));
  }

  if (Authentication.hasModuleAndOperationAccess(userData, Modules.PARKING_HISTORY, Operations.READ)) {
    items.push(getItem("Parking History", SideMenuItemKeys.PARKING_HISTORY, <i className="icon icon-frequent" />));
  }
  if (Authentication.hasModuleAndOperationAccess(userData, Modules.USER_MANAGEMENT, Operations.READ)) {
    items.push(getItem("User Management", SideMenuItemKeys.USER_MANAGEMENT, <i className="icon icon-user" />));
  }

  if (Authentication.hasModuleAndOperationAccess(userData, Modules.MASTER_MANAGEMENT, Operations.READ)) {
    items.push(getItem("Master Management", SideMenuItemKeys.MASTER_MANAGEMENT, <i className="icon icon-product-list" />));
  }

  if (Authentication.hasModuleAndOperationAccess(userData, Modules.USER_FEEDBACK, Operations.READ)) {
    items.push(getItem("User Feedback", SideMenuItemKeys.USER_FEEDBACK, <i className="icon icon-feedback" />));
  }

  if (Authentication.hasModuleAndOperationAccess(userData, Modules.MESSAGING, Operations.READ)) {
    items.push(getItem("Messaging", SideMenuItemKeys.MESSAGING, <i className="icon icon-product-list" />));
  }

  if (Authentication.hasModuleAndOperationAccess(userData, Modules.SUPPORT_QUERY, Operations.READ)) {
    items.push(getItem(`Support Query ${openCount > 0 ? `(${openCount})` : ""}`, SideMenuItemKeys.SUPPORT_QUERY, <i className="icon icon-contacts" />));
  }

  if (Authentication.hasModuleAndOperationAccess(userData, Modules.ADVANCE_BOOKING_REQUEST_LIST, Operations.READ)) {
    items.push(getItem("Advance Booking Request", SideMenuItemKeys.ADVANCE_BOOKING_REQUEST, <i className="icon icon-calendar" />));
  }

  /* Developer Options */
  if (userData?.role === UserRoleDATA.PARI_ADMIN) {
    const developerSubItems = [getItem("IoTHub Logs", SideMenuItemKeys.IOTHUB_LOGS, <i className="icon icon-breadcrumb" />), getItem("Concurrent Request", SideMenuItemKeys.CONCURRENT_REQUEST, <i className="icon icon-breadcrumb" />)];
    items.push(getItem("Developer Options", SideMenuItemKeys.DEVELOPER_OPTIONS, <i className="icon icon-breadcrumb" />, developerSubItems));
  }

  /* Reports */
  const reportSubItems = [];

  if (Authentication.hasModuleAndOperationAccess(userData, Modules.REQUEST_PER_HOUR_REPORT, Operations.READ)) {
    reportSubItems.push(getItem("Request/Hour Report", SideMenuItemKeys.REQUEST_PER_HOUR_REPORT, <i className="icon icon-card" />));
  }

  if (Authentication.hasModuleAndOperationAccess(userData, Modules.VISITORS_REPORT, Operations.READ)) {
    reportSubItems.push(getItem("Visitors Report", SideMenuItemKeys.VISITORS_REPORT, <i className="icon icon-card" />));
  }

  if (Authentication.hasModuleAndOperationAccess(userData, Modules.ALL_USERS_PARKING_REPORT, Operations.READ)) {
    reportSubItems.push(getItem("All Users Parking Report", SideMenuItemKeys.ALL_USERS_PARKING_REPORT, <i className="icon icon-card" />));
  }

  if (Authentication.hasModuleAndOperationAccess(userData, Modules.PARKING_DATA_COUNTS_REPORT, Operations.READ)) {
    reportSubItems.push(getItem("Parking Data Counts", SideMenuItemKeys.PARKING_DATA_COUNTS_REPORT, <i className="icon icon-card" />));
  }

  if (Authentication.hasModuleAndOperationAccess(userData, Modules.ADVANCE_BOOKING_REQUEST_REPORT, Operations.READ)) {
    reportSubItems.push(getItem("Advance Booking Request Report", SideMenuItemKeys.ADVANCE_BOOKING_REQUEST_REPORT, <i className="icon icon-card" />));
  }

  if (Authentication.hasModuleAndOperationAccess(userData, Modules.ABR_PENALTY_COLLECTION_REPORT, Operations.READ)) {
    reportSubItems.push(getItem("Penalty Collection Report", SideMenuItemKeys.ABR_PENALTY_COLLECTION_REPORT, <i className="icon icon-card" />));
  }

  if (reportSubItems.length > 0) {
    const reportMenuItem = getItem("Reports", SideMenuItemKeys.REPORT_SECTION, <i className="icon icon-product-list" />, reportSubItems);
    items.push(reportMenuItem);
  }

  return items;
};

const SideBarContent = ({ sidebarCollapsed, setSidebarCollapsed, isSmallScreen }: SidebarContentProps) => {
  const config = useSideBarContent();

  const openCount = useSelector((state: RootState) => state.common.openCount);

  const items: MenuItem[] = generateHomeSettings(openCount);

  return (
    <>
      {/* hamburger icon */}
      <SidebarLogo sidebarCollapsed={sidebarCollapsed} setSidebarCollapsed={setSidebarCollapsed} />
      <div style={{ height: "100%" }}>
        {isSmallScreen ? (
          <Menu onClick={config.handleOnClick} className={styles["menu"]} selectedKeys={[config.selectedKeys]} theme={config.themeType === THEME_TYPE_LITE ? "light" : "dark"} mode="inline" items={items}></Menu>
        ) : (
          <CustomScrollbars style={{ height: "90vh" }}>
            <Menu onClick={config.handleOnClick} className={styles["menu"]} selectedKeys={[config.selectedKeys]} theme={config.themeType === THEME_TYPE_LITE ? "light" : "dark"} mode="inline" items={items}></Menu>
          </CustomScrollbars>
        )}
      </div>
    </>
  );
};

export default React.memo(SideBarContent);
